import { useEffect, useState } from 'react';
import { Table } from 'antd';
import { IConcept, IConceptEval, IVariable } from '../../../interfaces/plan';

interface Props {
    conceptEval: IConceptEval;
    concept: IConcept;
    loading: boolean;
    variables: IVariable[];
}

interface IColumn {
    title: string;
    dataIndex: string;
    key: string;
    width?: number;
    render?: (data: any) => React.ReactNode;
}

const TableOperations = ({
    conceptEval,
    concept,
    loading,
    variables,
}: Props) => {
    const [dataSource, setDataSource] = useState<any[]>([]);
    const { operation } = concept;

    const columns: IColumn[] = [
        {
            title: 'Variable',
            dataIndex: 'variable',
            key: 'variable',
            width: 150,
        },
        {
            title: 'Operator',
            dataIndex: 'operator',
            key: 'operator',
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
        },
    ];

    const getObjectFields = (object: any, field: string): any => {
        const fieldParts = field.split('.');
        let currentField = object;

        for (const part of fieldParts) {
            if (currentField && typeof currentField === 'object' && part in currentField) {
                currentField = currentField[part];
            } else {
                return undefined;
            }
        }
        return currentField;
    };

    const getTypeValue = (value: any): string => {
        if (value === null) {
            return 'null';
        }
        if (typeof value === 'string') {
            return `"${value}"`;
        }
        if (Array.isArray(value)) {
            return `[${value.map(getTypeValue).join(', ')}]`;
        }
        if (typeof value === 'object') {
            return `{${Object.entries(value).map(([key, val]) => `${key}: ${getTypeValue(val)}`).join(', ')}}`;
        }
        if (typeof value === 'number') {
            return value.toString();
        }
        if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }
        if (typeof value === 'undefined') {
            return 'undefined';
        }
        return value;
    };

    const getOperations = () => {
        let expressionWithValues = operation;

        /* Replace variables */
        const variableRegex = /{{variable.(.*?)}}/g;
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        expressionWithValues = expressionWithValues.replace(variableRegex, (match, property) => {
            const variable = variables.find((v) => v._id.toString() === property);
            const valor = variable ? variable.value.toString() : undefined;

            setDataSource((prev) => [...prev, {
                variable: variable?.name,
                operator: match,
                value: valor || '0',
            }]);
            return valor || '0';
        });

        /* Replace booking data */
        const bookingRegex = /{{(booking|workspace).(.*?)}}/g;

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        expressionWithValues = expressionWithValues.replace(bookingRegex, (match, entity, property) => {
            let valor = getObjectFields(conceptEval?.data, property);
            if (Array.isArray(valor)) {
                valor = JSON.stringify(valor);
            }
            setDataSource((prev) => [...prev, { variable: entity, operator: match, value: getTypeValue(valor) }]);
            return valor;
        });
    };

    useEffect(() => {
        getOperations();
    }, []);

    useEffect(() => {
        if (!conceptEval) setDataSource([]);
    }, [conceptEval]);

    return (
        <Table
            pagination={false}
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            scroll={{ x: 800, y: 300 }}
        />
    );
};

export default TableOperations;
